import React from 'react';
import { motion } from 'framer-motion';

/**
 * The philosophical centre of the site.
 * A single teal source flows down, is breached, and fractures into
 * coral pirate streams via organic curved paths.
 */
const LeakBeam = () => {
  const W = 600;
  const H = 480;
  const sourceY = 78;
  const breachY = 250;
  const endY = 410;

  const endpoints = [
    { x: 90, id: '01' },
    { x: 215, id: '02' },
    { x: 385, id: '03' },
    { x: 510, id: '04' },
  ];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full max-w-[560px] mx-auto"
      role="presentation"
      aria-hidden
    >
      <defs>
        <linearGradient id="srcGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#00D4AA" stopOpacity="0.15" />
          <stop offset="30%" stopColor="#00D4AA" stopOpacity="1" />
          <stop offset="100%" stopColor="#00D4AA" stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="leakGrad" x1="0.5" x2="0.5" y1="0" y2="1">
          <stop offset="0%" stopColor="#FF6B3D" stopOpacity="0.95" />
          <stop offset="65%" stopColor="#FF6B3D" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#FF6B3D" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="breachGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#FF6B3D" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#FF6B3D" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#FF6B3D" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sourceGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#00D4AA" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#00D4AA" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Top frame: label + rule */}
      <motion.text
        x={W / 2}
        y="26"
        textAnchor="middle"
        fill="#00D4AA"
        className="font-mono"
        fontSize="10"
        letterSpacing="4"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        YOUR SYSTEM
      </motion.text>
      <motion.line
        x1="210"
        x2="390"
        y1="40"
        y2="40"
        stroke="#00D4AA"
        strokeOpacity="0.25"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.1 }}
      />

      {/* Source aura + node */}
      <motion.circle
        cx={W / 2}
        cy={sourceY}
        r="40"
        fill="url(#sourceGlow)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.3 }}
      />
      <motion.circle
        cx={W / 2}
        cy={sourceY}
        r="20"
        stroke="#00D4AA"
        strokeOpacity="0.35"
        strokeWidth="1"
        fill="none"
        animate={{ r: [18, 30], opacity: [0.45, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeOut' }}
      />
      <motion.circle
        cx={W / 2}
        cy={sourceY}
        r="7"
        fill="#00D4AA"
        initial={{ opacity: 0, scale: 0.4 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.35 }}
      />

      {/* Main teal beam */}
      <motion.line
        x1={W / 2}
        x2={W / 2}
        y1={sourceY + 10}
        y2={breachY - 6}
        stroke="url(#srcGrad)"
        strokeWidth="2.5"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.55 }}
      />

      {/* Flowing particle on beam */}
      <motion.circle
        r="2.5"
        fill="#00D4AA"
        cx={W / 2}
        initial={{ cy: sourceY + 10, opacity: 0 }}
        animate={{
          cy: [sourceY + 10, breachY - 6],
          opacity: [0, 1, 1, 0],
        }}
        transition={{
          duration: 1.6,
          repeat: Infinity,
          delay: 1.5,
          ease: 'easeIn',
          times: [0, 0.15, 0.85, 1],
        }}
      />

      {/* Breach glow halo */}
      <motion.circle
        cx={W / 2}
        cy={breachY}
        r="70"
        fill="url(#breachGlow)"
        initial={{ opacity: 0, scale: 0.6 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 1.2 }}
      />

      {/* Breach ring */}
      <motion.circle
        cx={W / 2}
        cy={breachY}
        r="14"
        fill="#0B1120"
        stroke="#FF6B3D"
        strokeWidth="1.25"
        initial={{ opacity: 0, scale: 0.5 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 1.4 }}
      />
      <motion.circle
        cx={W / 2}
        cy={breachY}
        r="4"
        fill="#FF6B3D"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 1.55 }}
      />
      <motion.circle
        cx={W / 2}
        cy={breachY}
        r="14"
        stroke="#FF6B3D"
        strokeOpacity="0.5"
        strokeWidth="1"
        fill="none"
        animate={{ r: [14, 30], opacity: [0.6, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut', delay: 1.6 }}
      />

      {/* Curved coral leak branches */}
      {endpoints.map((e, i) => {
        const dx = e.x - W / 2;
        const c1x = W / 2 + dx * 0.05;
        const c1y = breachY + 55;
        const c2x = e.x;
        const c2y = breachY + 105;
        const d = `M ${W / 2} ${breachY + 14} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${e.x} ${endY - 10}`;
        return (
          <motion.path
            key={e.id}
            d={d}
            stroke="url(#leakGrad)"
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 1.7 + i * 0.1, ease: [0.6, 0, 0.2, 1] }}
          />
        );
      })}

      {/* Endpoint nodes + IDs */}
      {endpoints.map((e, i) => (
        <g key={e.id}>
          <motion.circle
            cx={e.x}
            cy={endY - 10}
            r="12"
            fill="#FF6B3D"
            fillOpacity="0.12"
            initial={{ opacity: 0, scale: 0.4 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 2.5 + i * 0.08 }}
          />
          <motion.circle
            cx={e.x}
            cy={endY - 10}
            r="4"
            fill="#FF6B3D"
            initial={{ opacity: 0, scale: 0.4 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 2.55 + i * 0.08 }}
          />
          <motion.text
            x={e.x}
            y={endY + 14}
            textAnchor="middle"
            fill="#FF6B3D"
            fillOpacity="0.75"
            className="font-mono"
            fontSize="9"
            letterSpacing="2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 2.65 + i * 0.08 }}
          >
            {e.id}
          </motion.text>
        </g>
      ))}

      {/* Bottom frame: rule + label */}
      <motion.line
        x1="180"
        x2="420"
        y1={H - 32}
        y2={H - 32}
        stroke="#FF6B3D"
        strokeOpacity="0.25"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 2.9 }}
      />
      <motion.text
        x={W / 2}
        y={H - 14}
        textAnchor="middle"
        fill="#FF6B3D"
        className="font-mono"
        fontSize="10"
        letterSpacing="4"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 3 }}
      >
        PIRATE STREAMS
      </motion.text>
    </svg>
  );
};

const Section03Insight = () => {
  return (
    <section className="relative section-pad section-glass-alt" id="insight">
      {/* transition divider */}
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal/20 to-transparent" />

      <div className="container-edge">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col gap-6 mb-20 md:mb-24 max-w-4xl"
        >
          <h2 className="font-display font-bold text-white leading-[0.95] tracking-tight text-section">
            Every pirate stream<br />
            <span className="text-teal">starts with a legitimate feed.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 space-y-8 max-w-xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-xl md:text-2xl text-white/90 font-normal leading-[1.4]"
            >
              Whether it&rsquo;s a teenager with a £10 capture card or a criminal
              network generating{' '}
              <span className="text-white font-medium">€250&nbsp;million a month</span>
              , the source is always inside your system.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="space-y-3 text-white/75 font-mono text-sm uppercase tracking-[0.3em]"
            >
              <li className="flex items-center gap-3">
                <span className="h-px w-8 bg-teal" />
                A subscriber account
              </li>
              <li className="flex items-center gap-3">
                <span className="h-px w-8 bg-teal" />
                A production feed
              </li>
              <li className="flex items-center gap-3">
                <span className="h-px w-8 bg-teal" />
                A CDN token
              </li>
            </motion.ul>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="text-2xl md:text-3xl font-display font-semibold text-white leading-[1.2] pt-4"
            >
              The pirate is downstream.<br />
              The source is upstream.{' '}
              <span className="text-teal">And the source is yours.</span>
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="lg:col-span-6"
          >
            <LeakBeam />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Section03Insight;
